var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useState, useCallback } from 'react';
// ─── Utilities ───────────────────────────────────────────────────────────────
function uid() {
    // Compatible with React 16.8+ — no useId
    return Math.random().toString(36).slice(2, 9);
}
/**
 * Contador para IDs temporales de tiers nuevos (aún no persistidos en BD).
 * Los IDs temporales son SIEMPRE negativos para distinguirlos de los IDs
 * reales que vienen de BD (siempre positivos).
 *
 * IMPORTANTE: estos IDs nunca deben enviarse al backend — filtrarlos antes
 * del POST/PUT eliminando el campo id cuando sea negativo.
 */
var _tempIdCounter = -1;
function nextTempId() {
    return _tempIdCounter--; // -1, -2, -3, ...
}
function makeTier(minPax, maxPax) {
    return {
        // id: uid(),
        id: nextTempId(),
        minPax: minPax,
        maxPax: maxPax,
        price: '',
        enabled: true,
        // money_id
    };
}
/**
 * Full re-stitching pass after any structural mutation (add / remove).
 *
 * Rules applied in order for each row:
 * 1. Row 0 always starts at minPax = 1.
 * 2. Every subsequent row gets minPax = prev.maxPax + 1  (no gaps).
 * 3. Last row is forced open-ended (maxPax = null) — the caller's normalize()
 *    will optionally clamp it to maxPaxCap afterwards.
 * 4. Any intermediate row whose maxPax < its new minPax is clamped up to
 *    minPax so the state never becomes incoherent.
 */
function reStitch(tiers) {
    var _a;
    var result = [];
    for (var i = 0; i < tiers.length; i++) {
        var tier = tiers[i];
        var isLast = i === tiers.length - 1;
        // Step 1 & 2: recalculate minPax — usando la fila anterior YA recalculada,
        // no la original, para que los cambios se propaguen en cascada.
        var newMinPax = void 0;
        if (i === 0) {
            newMinPax = 1;
        }
        else {
            var prev = result[i - 1];
            newMinPax = ((_a = prev.maxPax) !== null && _a !== void 0 ? _a : tier.minPax - 1) + 1;
        }
        // Step 3: el último tier tiene maxPax a null
        if (isLast) {
            result.push(__assign(__assign({}, tier), { minPax: newMinPax, maxPax: null }));
            continue;
        }
        // Step 4: limitio maxPax a newMinPax si cae por debajo
        var newMaxPax = tier.maxPax !== null && tier.maxPax < newMinPax
            ? newMinPax
            : tier.maxPax;
        result.push(__assign(__assign({}, tier), { minPax: newMinPax, maxPax: newMaxPax }));
    }
    return result;
}
export function useTierLogic(initialTiers, maxPaxCap) {
    var _a = useState(function () {
        if (initialTiers && initialTiers.length > 0)
            return initialTiers;
        return [makeTier(1, maxPaxCap !== null && maxPaxCap !== void 0 ? maxPaxCap : null)];
    }), tiers = _a[0], setTiers = _a[1];
    // ── Helpers ────────────────────────────────────────────────────────────────
    /**
     * Final normalization pass: called after reStitch (or directly for
     * addTier / updateTier). Applies the optional maxPaxCap to the last
     * tier and clamps any remaining intermediate inconsistencies.
     */
    var normalize = useCallback(function (raw) {
        var restitched = reStitch(raw);
        return restitched.map(function (t, i) {
            if (i === restitched.length - 1) {
                // Last tier: honour maxPaxCap when set, otherwise leave open (null)
                return __assign(__assign({}, t), { maxPax: maxPaxCap !== null && maxPaxCap !== void 0 ? maxPaxCap : null });
            }
            // Intermediate tier: clamp maxPax up to minPax if still inconsistent
            if (t.maxPax !== null && t.maxPax < t.minPax) {
                return __assign(__assign({}, t), { maxPax: t.minPax });
            }
            return t;
        });
    }, [maxPaxCap]);
    // ── Actions ────────────────────────────────────────────────────────────────
    var DEFAULT_INCREMENT_TIERS = 0;
    var addTier = useCallback(function () {
        console.log('addTier');
        setTiers(function (prev) {
            var last = prev[prev.length - 1];
            if (!last) {
                console.log('addTier - prev: ', prev);
                console.log('addTier - initialTiers: ', initialTiers);
                return [makeTier(1, maxPaxCap !== null && maxPaxCap !== void 0 ? maxPaxCap : null)];
                return prev;
            }
            // Derive new tier's start from the previous last tier's max
            var prevMax = last.maxPax !== null ? last.maxPax : last.minPax + DEFAULT_INCREMENT_TIERS;
            var newMinPax = prevMax + 1;
            // Give the currently-last tier an explicit maxPax so it's no longer open
            var updatedLast = __assign(__assign({}, last), { maxPax: prevMax });
            var newTier = makeTier(newMinPax, null);
            var next = __spreadArray(__spreadArray([], prev.slice(0, -1), true), [updatedLast, newTier], false);
            return normalize(next);
        });
    }, [normalize]);
    var removeTier = useCallback(function (id) {
        setTiers(function (prev) {
            // ── Rule: existence guard ─────────────────────────────────────────
            // Always keep at least one tier; silently ignore the call if only
            // one remains (the button is also disabled in the UI at this point).
            // if (prev.length <= 1) return prev
            // ── Step 1: remove the target row ────────────────────────────────
            var filtered = prev.filter(function (t) { return t.id !== id; });
            // ── Step 2: re-stitch gaps + enforce last-tier open-end ──────────
            // reStitch recalculates every minPax, clamps maxPax where needed,
            // and forces the final row's maxPax to null.
            var restitched = reStitch(filtered);
            // ── Step 3: apply maxPaxCap to the new last tier (if configured) ─
            // normalize() does this, but we call it after reStitch so the
            // cap wins over the null we just set.
            return normalize(restitched);
        });
    }, [normalize]);
    var updateTier = useCallback(function (id, patch) {
        setTiers(function (prev) {
            var idx = prev.findIndex(function (t) { return t.id === id; });
            if (idx === -1)
                return prev;
            var updated = __assign(__assign({}, prev[idx]), patch);
            // Validate: maxPax must be >= minPax (for non-last, non-null rows)
            if (updated.maxPax !== null &&
                updated.maxPax < updated.minPax) {
                updated.maxPax = updated.minPax;
            }
            var next = __spreadArray(__spreadArray(__spreadArray([], prev.slice(0, idx), true), [
                updated
            ], false), prev.slice(idx + 1), true);
            // If maxPax changed on an intermediate row, propagate continuity
            if ('maxPax' in patch) {
                return normalize(next);
            }
            return next;
        });
    }, [normalize]);
    var toggleTier = useCallback(function (id) {
        setTiers(function (prev) {
            return prev.map(function (t) { return (t.id === id ? __assign(__assign({}, t), { enabled: !t.enabled }) : t); });
        });
    }, []);
    return { tiers: tiers, addTier: addTier, removeTier: removeTier, updateTier: updateTier, toggleTier: toggleTier };
}



//////////////////
// WEBPACK FOOTER
// ./src/components/PricingTier/hooks/useTierLogic.ts
// module id = 412
// module chunks = 0